// The tab row's view model: the server's open drafts as tabs (tab-model.js), each marked
// with its save state as the create screen reports it through DraftsProvider's
// `reportStatus`. Pure, for node --test (test/web/tab-row.test.js); DraftTabs.jsx only
// draws what this returns.
//
// The markers (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md, "The tab
// row"): `●` while a change is not yet saved, `⚠` after a save that did not land — failed,
// refused as stale, or refused because a lead took the draft over. A report is a
// draft-sync.js snapshot, `{ state, revision, ...extras }`.
//
// A draft whose FIRST save never landed (a 401 redirect, a crash, a reboot) is not on the
// server's list, and no screen reports it; its text is only in this browser's write-ahead
// buffer (draft-buffer.js), at base revision 0. `recoverableDraftIds` picks those out of the
// signed-in user's buffer entries, and the row gives each a tab, so the spec's promise that
// a crash loses nothing holds: opening it replays the buffer (pages/tickets/New.jsx).

import { toTabs } from './tab-model.js';
import { draftHref } from './draft-address.js';
import { isDraftId } from './draft-model.js';

const PENDING = { glyph: '●', tone: 'pending', text: 'not saved yet' };
const PROBLEMS = {
  failed: 'save failed',
  stale: 'changed on another PC',
  taken: 'taken over',
};

/** `{ glyph, tone, text }` for a snapshot, or null when there is nothing to flag. */
export function markerFor(snapshot) {
  const state = snapshot?.state;
  if (state === 'dirty' || state === 'saving') return { ...PENDING };
  if (Object.hasOwn(PROBLEMS, state ?? '')) return { glyph: '⚠', tone: 'problem', text: PROBLEMS[state] };
  return null;
}

// Closed by the agent on this screen: the tab goes now, not when the list next refreshes.
const CLOSED = new Set(['filed', 'discarded']);
// A draft the server may not have yet (its first save has not landed) still gets a tab
// while there is something in it to lose.
const UNSAVED = new Set(['dirty', 'saving', 'failed']);

// Not listed but worth a tab: unsaved work, or a draft whose save HAS landed (idle at a
// revision above 0) a round trip before GET /api/ticket-drafts lists it — without this the
// row vanished between a new draft's first save and the refresh that save triggers. Idle
// at revision 0 is a draft nothing has been typed into, and gets no tab.
function keepsUnlistedTab(snapshot) {
  if (UNSAVED.has(snapshot.state)) return true;
  return snapshot.state === 'idle' && snapshot.revision > 0;
}

/**
 * The drafts in `pending` (draft-buffer.js `pending(userId)`, so already ONE user's) that
 * the server has never saved: built on base revision 0. An entry built on a saved revision
 * is a draft the server lists (or has closed), so it needs no tab of its own.
 */
export function recoverableDraftIds(pending) {
  return (pending ?? [])
    .filter((entry) => entry?.baseRevision === 0 && isDraftId(entry.draftId))
    .map((entry) => entry.draftId);
}

function entries(statuses) {
  if (!statuses) return [];
  return statuses instanceof Map ? [...statuses.entries()] : Object.entries(statuses);
}

/**
 * @param {object[]} drafts            `GET /api/ticket-drafts` rows.
 * @param {object|Map} statuses        draft id → the latest reported snapshot (or null).
 * @param {number|Date} now
 * @param {string[]} [recovered]       `recoverableDraftIds` of the signed-in user's buffer.
 * @returns {{ kind, id, label, href, age, marker }[]} The server's drafts oldest first,
 *   less any reported filed or discarded, then reported drafts the list does not have
 *   yet — holding unsaved work, or saved but not yet listed — labelled "New ticket" with
 *   no age, in the order they were reported; then each recovered draft that is neither
 *   listed nor reported (a reported one is left to its report), labelled the same and
 *   marked as not saved.
 */
export function tabRow(drafts, statuses, now, recovered = []) {
  const reported = new Map(entries(statuses).filter(([, snapshot]) => snapshot));
  const listed = toTabs(drafts ?? [], now)
    .filter((tab) => !CLOSED.has(reported.get(tab.id)?.state))
    .map((tab) => ({ ...tab, marker: markerFor(reported.get(tab.id)) }));
  const known = new Set((drafts ?? []).map((draft) => draft.id));
  const unlisted = [...reported]
    .filter(([id, snapshot]) => !known.has(id) && keepsUnlistedTab(snapshot))
    .map(([id, snapshot]) => ({
      kind: 'draft', id, label: 'New ticket', href: draftHref(id), age: '', marker: markerFor(snapshot),
    }));
  // GUIDs compared case-blind: the server's list and a browser-minted id may differ in case.
  const seen = new Set([...known, ...reported.keys()].map((id) => String(id).toLowerCase()));
  const fromBuffer = [];
  for (const id of recovered ?? []) {
    const lower = String(id).toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    fromBuffer.push({ kind: 'draft', id, label: 'New ticket', href: draftHref(id), age: '', marker: { ...PENDING } });
  }
  return [...listed, ...unlisted, ...fromBuffer];
}

/** The tab's accessible name: what its label, age and marker glyph say, in words. */
export function tabAccessibleName(tab) {
  const parts = [`Draft ${tab.label}`];
  if (tab.age === 'now') parts.push('started just now');
  else if (tab.age) parts.push(`started ${tab.age} ago`);
  if (tab.marker) parts.push(tab.marker.text);
  return parts.join(', ');
}
